const safeSessionStorage = () => {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch (_) {
    return null;
  }
};

const requestKeyMemory = new Map();

const stableStringify = (value) => {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }
  if (value && typeof value === "object") {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value ?? null);
};

export const buildRequestFingerprint = (details) => stableStringify(details);

const createRequestKey = (prefix) => {
  const safePrefix = String(prefix || "request")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 32) || "request";
  const browserCrypto = typeof window !== "undefined" ? window.crypto : null;
  const randomId = browserCrypto?.randomUUID?.()
    || `${Date.now()}-${Math.random().toString(36).slice(2, 14)}`;
  return `${safePrefix}:${randomId}`;
};

export const getStableRequestKey = ({ storageKey, fingerprint, prefix }) => {
  const storage = safeSessionStorage();
  if (storage) {
    try {
      const stored = JSON.parse(storage.getItem(storageKey) || "null");
      if (stored?.fingerprint === fingerprint && stored?.requestKey) {
        requestKeyMemory.set(storageKey, stored);
        return stored.requestKey;
      }
    } catch (_) {
      // Replace invalid or unavailable browser storage with a fresh key.
    }
  }

  const remembered = requestKeyMemory.get(storageKey);
  if (remembered?.fingerprint === fingerprint && remembered?.requestKey) {
    return remembered.requestKey;
  }

  const requestKey = createRequestKey(prefix);
  requestKeyMemory.set(storageKey, { fingerprint, requestKey });
  if (storage) {
    try {
      storage.setItem(storageKey, JSON.stringify({ fingerprint, requestKey }));
    } catch (_) {
      // The key still protects repeat clicks during this request.
    }
  }
  return requestKey;
};

export const clearStableRequestKey = ({ storageKey, requestKey }) => {
  const remembered = requestKeyMemory.get(storageKey);
  if (!requestKey || remembered?.requestKey === requestKey) {
    requestKeyMemory.delete(storageKey);
  }
  const storage = safeSessionStorage();
  if (!storage) return;
  try {
    const stored = JSON.parse(storage.getItem(storageKey) || "null");
    if (!requestKey || stored?.requestKey === requestKey) {
      storage.removeItem(storageKey);
    }
  } catch (_) {
    storage.removeItem(storageKey);
  }
};
